import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  companyNationalIdInputProps,
  yupCompanyNationalIdOptional,
} from "../../../../../infrastructure/utils/companyNationalIdRules";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import TabPanel from "../../../../components/shared/tab-panel";

function a11yProps(index) {
  return { id: `contractor-tab-${index}`, "aria-controls": `contractor-tabpanel-${index}` };
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required("لطفا نام شرکت را وارد کنید."),
  mobile: Yup.string()
    .required("لطفا شماره موبایل را وارد کنید.")
    .matches(/^09\d{9}$/, "شماره موبایل معتبر نیست"),
  email: Yup.string().email("ایمیل معتبر نیست"),
  about: Yup.string().required("لطفا متن معرفی را وارد کنید."),
  companyNationalId: yupCompanyNationalIdOptional(),
});

const ContractorInfoModifyPage = () => {
  const data = useLoaderData();
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const baseUrl = window.globalConfig?.API_URL || "";
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [provinces, setProvinces] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);
  const [cityPermission, setCityPermission] = useState(data?.cityPermission ?? []);
  const [expandedProvinces, setExpandedProvinces] = useState([]);

  // Load provinces, cities and specialties
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [provincesRes, citiesRes, specialtiesRes] = await Promise.all([
          DefaultApiService.getApi("SLN/Management/ContractorInfo/GetProvinces"),
          DefaultApiService.getApi("SLN/Management/ContractorInfo/GetCities"),
          DefaultApiService.getApi("SLN/Management/ContractorInfo/GetSpecialties"),
        ]);

        if (provincesRes?.isSuccess && provincesRes?.data) {
          setProvinces(provincesRes.data);
        }
        if (citiesRes?.isSuccess && citiesRes?.data) {
          setAllCities(citiesRes.data);
        }
        if (specialtiesRes?.isSuccess && specialtiesRes?.data) {
          setSpecialties(specialtiesRes.data);
        }
      } catch (error) {
        console.error("خطا در دریافت اطلاعات:", error);
        toast.error("خطا در بارگذاری اطلاعات");
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  // Initialize selected province from data
  useEffect(() => {
    const contractorInfo = data?.contractorInfo || {};
    if (contractorInfo?.provinceId) {
      setSelectedProvinceId(contractorInfo.provinceId);
    }
  }, [data?.contractorInfo?.provinceId]);

  // Initialize cityPermission from data
  useEffect(() => {
    setCityPermission(data?.cityPermission ?? []);
  }, [data?.cityPermission]);

  // Filter cities based on selected province (for form address)
  const filteredCities = selectedProvinceId
    ? allCities.filter((city) => city.provinceId === parseInt(selectedProvinceId))
    : [];

  // City permission handlers
  const onCheckedCityPermission = (event, city) => {
    const checked = event.target.checked;
    setCityPermission((prev) =>
      prev.map((x) => (x.cityId === city.cityId ? { ...x, allowed: checked } : x))
    );
  };

  const onHandleAllCheckedCity = (isChecked) => {
    setCityPermission((prev) => prev.map((x) => ({ ...x, allowed: isChecked })));
  };

  const onToggleProvinceCities = (provinceId, isChecked) => {
    setCityPermission((prev) =>
      prev.map((x) => {
        const meta = allCities.find((c) => c.id === x.cityId);
        if (meta && meta.provinceId === provinceId) return { ...x, allowed: isChecked };
        return x;
      })
    );
  };

  // Province groups for city permissions (using useMemo for performance)
  const provinceGroups = React.useMemo(() => {
    if (!allCities.length || !provinces.length || !Array.isArray(cityPermission)) return [];
    const map = new Map();
    allCities.forEach((c) => {
      const perm = cityPermission.find((p) => p.cityId === c.id);
      const allowed = perm ? perm.allowed : false;
      let group = map.get(c.provinceId);
      if (!group) {
        const province = provinces.find((p) => p.id === c.provinceId);
        group = {
          provinceId: c.provinceId,
          provinceName: province?.title || `استان ${c.provinceId}`,
          cities: [],
        };
        map.set(c.provinceId, group);
      }
      group.cities.push({ cityId: c.id, cityName: c.cityName, allowed });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.provinceName.localeCompare(b.provinceName, "fa")
    );
  }, [allCities, provinces, cityPermission]);

  const isProvinceExpanded = (provinceId) => expandedProvinces.includes(provinceId);
  const toggleProvinceExpanded = (provinceId) => {
    setExpandedProvinces((prev) =>
      prev.includes(provinceId) ? prev.filter((id) => id !== provinceId) : [...prev, provinceId]
    );
  };

  const onNavigateToListPage = () => {
    navigate("/SLN/Management/ContractorInfo");
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    
    // Prepare data with cityPermission
    const sendData = {
      contractorInfo: formValue,
      cityPermission: cityPermission.map((p) => ({
        cityId: p.cityId,
        cityName: p.cityName,
        allowed: p.allowed
      }))
    };

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/Management/ContractorInfo",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message || "اطلاعات با موفقیت ذخیره شد");
        onNavigateToListPage();
      } else {
        toast.error(res.message || "خطا در ذخیره اطلاعات");
      }

      setLoading(false);
    } catch (error) {
      console.error("خطا در ذخیره:", error);
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  // Determine the contractor info structure
  // API may return data directly or nested in contractorInfo
  const contractorInfo = data?.contractorInfo || {};

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ${contractorInfo?.title || "پیمانکار"}`
                : "ایجاد پیمانکار جدید"
            }
          />

          <CardContent>
            {loadingData ? (
              <Grid container justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
              </Grid>
            ) : (
              <Formik
                initialValues={contractorInfo}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                }) => {
                  const onMakeFormikVariable = (name) => ({
                    name: name,
                    fullWidth: true,
                    value: values[name] || "",
                    error: errors[name] && touched[name],
                    helperText: errors[name] && touched[name] && errors[name],
                    variant: "outlined",
                  });

                  return (
                    <form onSubmit={handleSubmit}>
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label="contractor tabs">
                          <Tab label="اطلاعات پیمانکار" {...a11yProps(0)} />
                          <Tab label="دسترسی شهرستان‌ها" {...a11yProps(1)} />
                        </Tabs>
                      </Box>

                      <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                          {/* وضعیت فعال/غیرفعال */}
                          <Grid item xs={12}>
                            <Card variant="outlined">
                              <CardContent>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.isActive || false}
                                      onChange={(e) =>
                                        setFieldValue("isActive", e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    values.isActive
                                      ? "پیمانکار فعال است"
                                      : "پیمانکار غیرفعال است"
                                  }
                                />
                              </CardContent>
                            </Card>
                          </Grid>

                          {/* اطلاعات شرکت */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardHeader title="اطلاعات شرکت" />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="نام شرکت"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("title")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="نام شرکت (انگلیسی)"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("companyName")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="شناسه ملی شرکت"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    inputProps={companyNationalIdInputProps}
                                    {...onMakeFormikVariable("companyNationalId")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="شماره ثبت شرکت"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable(
                                      "companyRegistrationNumber"
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="تلفن شرکت"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("companyPhone")}
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* اطلاعات تماس */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardHeader title="اطلاعات تماس" />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="نام"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("firstName")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="نام خانوادگی"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("lastName")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="کد ملی"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("nationalCode")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="موبایل"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("mobile")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="تلفن"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("telephon")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="ایمیل"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("email")}
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* آدرس و موقعیت */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardHeader title="آدرس و موقعیت" />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={errors.provinceId && touched.provinceId}
                                  >
                                    <InputLabel>استان</InputLabel>
                                    <Select
                                      label="استان"
                                      value={
                                        values.provinceId !== null &&
                                        values.provinceId !== undefined
                                          ? values.provinceId
                                          : ""
                                      }
                                      onChange={(e) => {
                                        const newProvinceId = e.target.value;
                                        const selectedProvince = provinces.find(
                                          (p) => p.id === newProvinceId
                                        );
                                        setFieldValue("provinceId", newProvinceId);
                                        setFieldValue(
                                          "province",
                                          selectedProvince?.title || ""
                                        );
                                        setFieldValue("city", "");
                                        setSelectedProvinceId(newProvinceId);
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>انتخاب کنید</em>
                                      </MenuItem>
                                      {provinces.map((province) => (
                                        <MenuItem key={province.id} value={province.id}>
                                          {province.title}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {errors.provinceId && touched.provinceId && (
                                      <FormHelperText>
                                        {errors.provinceId}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormControl
                                    fullWidth
                                    variant="outlined"
                                    disabled={!selectedProvinceId}
                                    error={errors.city && touched.city}
                                  >
                                    <InputLabel>شهر</InputLabel>
                                    <Select
                                      label="شهر"
                                      value={values.city || ""}
                                      onChange={(e) => {
                                        setFieldValue("city", e.target.value);
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>انتخاب کنید</em>
                                      </MenuItem>
                                      {filteredCities.map((city) => (
                                        <MenuItem
                                          key={city.id}
                                          value={city.cityName}
                                        >
                                          {city.cityName}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {errors.city && touched.city && (
                                      <FormHelperText>{errors.city}</FormHelperText>
                                    )}
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="کد پستی"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("postalCode")}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    label="آدرس"
                                    multiline
                                    minRows={3}
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("address")}
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* سایر اطلاعات */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardHeader title="سایر اطلاعات" />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="سابقه کاری (سال)"
                                    type="number"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("workExperience")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label="رتبه"
                                    type="number"
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("grade")}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <FormControl
                                    fullWidth
                                    error={errors.specialties && touched.specialties}
                                  >
                                    <InputLabel>تخصص‌ها و مهارت‌ها</InputLabel>
                                    <Select
                                      multiple
                                      label="تخصص‌ها و مهارت‌ها"
                                      value={(() => {
                                        // Parse specialties from string to array
                                        if (!values.specialties) return [];
                                        if (Array.isArray(values.specialties))
                                          return values.specialties;
                                        try {
                                          const parsed = JSON.parse(
                                            values.specialties
                                          );
                                          return Array.isArray(parsed) ? parsed : [];
                                        } catch {
                                          return [];
                                        }
                                      })()}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Store as JSON string
                                        setFieldValue(
                                          "specialties",
                                          JSON.stringify(value)
                                        );
                                      }}
                                      renderValue={(selected) => (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                          }}
                                        >
                                          {selected.map((value) => {
                                            const specialty = specialties.find(
                                              (s) => s.id === value
                                            );
                                            return specialty ? (
                                              <Chip
                                                key={value}
                                                label={specialty.title}
                                                size="small"
                                              />
                                            ) : null;
                                          })}
                                        </Box>
                                      )}
                                    >
                                      {specialties.map((specialty) => (
                                        <MenuItem
                                          key={specialty.id}
                                          value={specialty.id}
                                        >
                                          {specialty.title}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {errors.specialties && touched.specialties ? (
                                      <FormHelperText>
                                        {errors.specialties}
                                      </FormHelperText>
                                    ) : (
                                      <FormHelperText>
                                        می‌توانید چندین مورد انتخاب کنید
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="توضیحات"
                                    multiline
                                    minRows={2}
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("description")}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="متن معرفی"
                                    multiline
                                    minRows={2}
                                    onChange={wrapFormikHandleChange(handleChange)}
                                    {...onMakeFormikVariable("about")}
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* مدارک و گواهی‌ها */}
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardHeader title="مدارک و گواهی‌ها" />
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <FileUploader
                                    moduleName={"SLN/Management/ContractorInfo"}
                                    maxFiles={1}
                                    label="تصویر کارت ملی"
                                    baseUrl={baseUrl}
                                    accept="image/*,.pdf"
                                    onChange={(value) => {
                                      setFieldValue("nationalCardImageUrl", value || "");
                                    }}
                                    placeholder={"تصویر کارت ملی را انتخاب کنید"}
                                    value={values.nationalCardImageUrl}
                                    error={
                                      errors.nationalCardImageUrl &&
                                      touched.nationalCardImageUrl
                                    }
                                    helperText={
                                      errors.nationalCardImageUrl &&
                                      touched.nationalCardImageUrl &&
                                      errors.nationalCardImageUrl
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <FileUploader
                                    moduleName={"SLN/Management/ContractorInfo"}
                                    maxFiles={1}
                                    label="مجوز شرکت"
                                    baseUrl={baseUrl}
                                    accept="image/*,.pdf"
                                    onChange={(value) => {
                                      setFieldValue("companyLicenseImageUrl", value || "");
                                    }}
                                    placeholder={"مجوز شرکت را انتخاب کنید"}
                                    value={values.companyLicenseImageUrl}
                                    error={
                                      errors.companyLicenseImageUrl &&
                                      touched.companyLicenseImageUrl
                                    }
                                    helperText={
                                      errors.companyLicenseImageUrl &&
                                      touched.companyLicenseImageUrl &&
                                      errors.companyLicenseImageUrl
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <FileUploader
                                    moduleName={"SLN/Management/ContractorInfo"}
                                    maxFiles={1}
                                    label="گواهی‌نامه"
                                    baseUrl={baseUrl}
                                    accept="image/*,.pdf"
                                    onChange={(value) => {
                                      setFieldValue("certificateUrl", value || "");
                                    }}
                                    placeholder={"فایل گواهی‌نامه را انتخاب کنید"}
                                    value={values.certificateUrl}
                                    error={errors.certificateUrl && touched.certificateUrl}
                                    helperText={
                                      errors.certificateUrl &&
                                      touched.certificateUrl &&
                                      errors.certificateUrl
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <FileUploader
                                    moduleName={"SLN/Management/ContractorInfo"}
                                    maxFiles={1}
                                    label="سند بیمه"
                                    baseUrl={baseUrl}
                                    accept="image/*,.pdf"
                                    onChange={(value) => {
                                      setFieldValue("insuranceDocumentUrl", value || "");
                                    }}
                                    placeholder={"فایل سند بیمه را انتخاب کنید"}
                                    value={values.insuranceDocumentUrl}
                                    error={
                                      errors.insuranceDocumentUrl &&
                                      touched.insuranceDocumentUrl
                                    }
                                    helperText={
                                      errors.insuranceDocumentUrl &&
                                      touched.insuranceDocumentUrl &&
                                      errors.insuranceDocumentUrl
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <FileUploader
                                    moduleName={"SLN/Management/ContractorInfo"}
                                    maxFiles={5}
                                    label="نمونه کارها"
                                    baseUrl={baseUrl}
                                    accept="image/*,.pdf"
                                    onChange={(value) => {
                                      const urls = Array.isArray(value) ? value : value ? [value] : [];
                                      setFieldValue("workSamplesUrls", JSON.stringify(urls));
                                    }}
                                    placeholder={"نمونه کارها را انتخاب کنید (حداکثر ۵ فایل)"}
                                    value={(() => {
                                      const v = values.workSamplesUrls;
                                      if (!v) return "";
                                      try {
                                        const arr = typeof v === "string" ? JSON.parse(v) : v;
                                        return Array.isArray(arr) ? arr : [];
                                      } catch {
                                        return [];
                                      }
                                    })()}
                                    error={
                                      errors.workSamplesUrls && touched.workSamplesUrls
                                    }
                                    helperText={
                                      errors.workSamplesUrls &&
                                      touched.workSamplesUrls &&
                                      errors.workSamplesUrls
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                      </TabPanel>

                      <TabPanel value={tabValue} index={1}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={12}>
                            <Button
                              color="primary"
                              variant="contained"
                              size="small"
                              onClick={() => onHandleAllCheckedCity(true)}
                              sx={{ mr: 1 }}
                            >
                              انتخاب همه
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => onHandleAllCheckedCity(false)}
                            >
                              حذف همه
                            </Button>
                          </Grid>
                          {provinceGroups.length > 0
                            ? provinceGroups.map((province) => {
                                const total = province.cities.length;
                                const selectedCount = province.cities.filter((c) => c.allowed).length;
                                const allChecked = total > 0 && selectedCount === total;
                                const someChecked = selectedCount > 0 && selectedCount < total;
                                const expanded = isProvinceExpanded(province.provinceId);
                                return (
                                  <Grid item xs={12} key={province.provinceId}>
                                    <Box
                                      sx={{
                                        border: "1px solid #eee",
                                        borderRadius: 1,
                                        p: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={allChecked}
                                              indeterminate={someChecked}
                                              onChange={(e) =>
                                                onToggleProvinceCities(
                                                  province.provinceId,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                          }
                                          label={province.provinceName}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            toggleProvinceExpanded(province.provinceId)
                                          }
                                          aria-expanded={expanded}
                                          aria-label={expanded ? "بستن شهرستان‌ها" : "نمایش شهرستان‌ها"}
                                        >
                                          {expanded ? (
                                            <ExpandLessIcon fontSize="small" />
                                          ) : (
                                            <ExpandMoreIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Box>
                                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        <Grid container spacing={1} sx={{ pl: 3, pt: 1, pb: 0.5 }}>
                                          {province.cities.map((city) => (
                                            <Grid
                                              item
                                              xs={12}
                                              md={4}
                                              lg={3}
                                              key={city.cityId}
                                            >
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={city.allowed}
                                                    onChange={(e) =>
                                                      onCheckedCityPermission(e, city)
                                                    }
                                                  />
                                                }
                                                label={city.cityName}
                                              />
                                            </Grid>
                                          ))}
                                        </Grid>
                                      </Collapse>
                                    </Box>
                                  </Grid>
                                );
                              })
                            : (Array.isArray(cityPermission) && cityPermission.length > 0
                                ? cityPermission.map((city) => (
                                    <Grid item xs={12} md={4} lg={3} key={city.cityId}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={city.allowed}
                                            onChange={(e) =>
                                              onCheckedCityPermission(e, city)
                                            }
                                          />
                                        }
                                        label={city.cityName}
                                      />
                                    </Grid>
                                  ))
                                : null)}
                        </Grid>
                      </TabPanel>

                      {/* دکمه‌های عملیات */}
                      <Grid
                        item
                        xs={12}
                        container
                        justifyContent={"flex-end"}
                        sx={{ mt: 2 }}
                      >
                        <Button
                          disabled={isSubmitting || loading}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting || loading}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </form>
                  );
                }}
              </Formik>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ContractorInfoModifyPage;
